import { NgClass } from '@angular/common';
import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import {
  CompiereDataGridFilterType,
  CompiereDataGridResponseJSON,
  DataStore,
  DataStoreRequest,
} from '@compiere-ws/models/compiere-data-json';
import { SpecificDataService } from '@compiere-ws/services/specific-data/specific-data.service';
import { OperatorFilterType } from '@iupics-components/models/universal-filter';
import GridViewUiComponent from '@iupics-components/standard/grid/grid-view-ui/grid-view-ui.component';
import ModalUiComponent from '@iupics-components/standard/layouts/modal-ui/modal-ui.component';
import { DynamicContainerDirective } from '@iupics-util/directives/dynamic-container.directive';
import { ApizGridUtils } from '@iupics-util/tools/apiz-grid.utils';
import { DateUtils } from '@iupics-util/tools/date.utils';
import { TranslateModule } from '@ngx-translate/core';
import { cloneDeep } from 'lodash';
import { ProgressBarModule } from 'primeng/progressbar';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { SidebarModule } from 'primeng/sidebar';
import { zip } from 'rxjs';
import AutocompleteEditorComponent from '../editor/autocomplete-editor.component';
import ButtonEditorComponent from '../editor/button-editor.component';
import SpecificWindowUiComponent from '../specific-window-ui/specific-window-ui.component';

@Component({
  selector: 'iu-bpartner-balance',
  templateUrl: './bpartner-balance.component.html',
  styleUrls: ['./bpartner-balance.component.scss'],
  standalone: true,
  imports: [
    ProgressBarModule,
    ScrollPanelModule,
    DynamicContainerDirective,
    SidebarModule,
    ModalUiComponent,
    // UniversalFilterUiComponent,
    // GridTabInfinityScrollUiComponent,
    NgClass,
    TranslateModule,
  ],
  providers: [SpecificDataService],
})
export default class BpartnerBalanceComponent extends SpecificWindowUiComponent implements OnInit, AfterViewInit {
  private readonly specificDataService = inject(SpecificDataService);

  frameworkComponents: any;
  dataStore: DataStore;

  constructor() {
    super();
    this.frameworkComponents = {
      buttonEditor: ButtonEditorComponent,
      autocompleteEditor: AutocompleteEditorComponent,
    };
  }

  ngOnInit(): void {
    super.ngOnInit();
    const adOrg = this.getFormStoreData('AD_Org_ID');
    if (adOrg?.id === 0) {
      this.setExcludedFiltersToGrid('BP accounting_table', ['AD_Org_ID']);
    } else {
      this.removeExcludedFiltersToGrid('BP accounting_table');
    }
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  notifyFromGridRefresh(gridView: GridViewUiComponent, dataStoreRequest: DataStoreRequest): void {
    const obsZip = [];
    // balance
    dataStoreRequest.compiereRequest.sortModel = undefined;
    dataStoreRequest.compiereRequest.headerCols = undefined;
    dataStoreRequest.compiereRequest.valueCols = [
      {
        id: 'DR',
        colId: 'DR',
        aggFunc: 'sum',
        field: 'DR',
        displayName: 'DR',
      },
      {
        id: 'CR',
        colId: 'CR',
        aggFunc: 'sum',
        field: 'CR',
        displayName: 'CR',
      },
    ];

    dataStoreRequest.compiereRequest = ApizGridUtils.cleanDataRequestForCompiereRequest(
      dataStoreRequest.compiereRequest
    );
    obsZip.push(this.store.getDataGrid(dataStoreRequest));

    // Balance (devise base)
    const dataStoreRequest2 = cloneDeep(dataStoreRequest);
    dataStoreRequest2.compiereRequest.sortModel = undefined;
    dataStoreRequest2.compiereRequest.filterModel['DueDate'] = {
      filterType: CompiereDataGridFilterType.DATE,
      values: [DateUtils.formatStr(new Date())],
      operators: [OperatorFilterType.LESSTHANOREQUAL],
    };
    // montantOuvertTotal
    dataStoreRequest2.compiereRequest.valueCols = [
      {
        id: 'OPENAMT_BASE',
        colId: 'OPENAMT_BASE',
        aggFunc: 'sum',
        field: 'OPENAMT_BASE',
        displayName: 'OPENAMT_BASE',
      },
    ];

    dataStoreRequest2.compiereRequest = ApizGridUtils.cleanDataRequestForCompiereRequest(
      dataStoreRequest2.compiereRequest
    );
    obsZip.push(this.store.getDataGrid(dataStoreRequest2));

    // total non echu && mintant ouvert total
    const dataStoreRequest3 = cloneDeep(dataStoreRequest);
    dataStoreRequest3.compiereRequest.sortModel = undefined;
    dataStoreRequest3.compiereRequest.valueCols = [
      {
        id: 'BASE_DR',
        colId: 'BASE_DR',
        aggFunc: 'sum',
        field: 'BASE_DR',
        displayName: 'BASE_DR',
      },
      {
        id: 'BASE_CR',
        colId: 'BASE_CR',
        aggFunc: 'sum',
        field: 'BASE_CR',
        displayName: 'BASE_CR',
      },
    ];

    dataStoreRequest3.compiereRequest = ApizGridUtils.cleanDataRequestForCompiereRequest(
      dataStoreRequest3.compiereRequest
    );
    obsZip.push(this.store.getDataGrid(dataStoreRequest3));

    // total non echu
    const dataStoreRequest4 = cloneDeep(dataStoreRequest);
    dataStoreRequest4.compiereRequest.sortModel = undefined;
    dataStoreRequest4.compiereRequest.filterModel['DueDate'] = {
      filterType: CompiereDataGridFilterType.DATE,
      values: [DateUtils.formatStr(new Date())],
      operators: [OperatorFilterType.BIGGER],
    };
    // total non echu
    dataStoreRequest4.compiereRequest.valueCols = [
      {
        id: 'OPENAMT_BASE',
        colId: 'OPENAMT_BASE',
        aggFunc: 'sum',
        field: 'OPENAMT_BASE',
        displayName: 'OPENAMT_BASE',
      },
    ];

    dataStoreRequest4.compiereRequest = ApizGridUtils.cleanDataRequestForCompiereRequest(
      dataStoreRequest4.compiereRequest
    );
    obsZip.push(this.store.getDataGrid(dataStoreRequest4));
    this.subscriptions.push(
      zip(...obsZip).subscribe(([response1, response2, response3, response4]: CompiereDataGridResponseJSON[]) => {
        // Balance (devise base)
        if (
          response1.data[0]['DR'] !== undefined &&
          response1.data[0]['DR'] !== null &&
          response1.data[0]['CR'] !== undefined &&
          response1.data[0]['CR'] !== null
        ) {
          const dr = response1.data[0]['DR'];
          const cr = response1.data[0]['CR'];
          const balance = dr - cr;
          this.setFormStoreData('Balance', balance.toFixed(2));
        }
        // montantOuvertTotal
        if (response2.data[0]['OPENAMT_BASE'] !== undefined && response2.data[0]['OPENAMT_BASE'] !== null) {
          const dueTotal = response2.data[0]['OPENAMT_BASE'];
          this.setFormStoreData('DueTotal', dueTotal.toFixed(2));
        }
        // const notDueTotal = this.getFormStoreData('Balance', 0) - this.getFormStoreData('DueTotal', 0);
        if (response4.data[0]['OPENAMT_BASE'] !== undefined && response4.data[0]['OPENAMT_BASE'] !== null) {
          const notDueTotal = response4.data[0]['OPENAMT_BASE'];
          this.setFormStoreData('NotDueTotal', notDueTotal.toFixed(2));
        }
        // total non echu && mintant ouvert total
        if (
          response3.data[0]['BASE_DR'] !== undefined &&
          response3.data[0]['BASE_DR'] !== null &&
          response3.data[0]['BASE_CR'] !== undefined &&
          response3.data[0]['BASE_CR'] !== null
        ) {
          const baseDR = response3.data[0]['BASE_DR'];
          const baseCR = response3.data[0]['BASE_CR'];
          const BALANCEBASE = baseDR - baseCR;
          this.setFormStoreData('Balance_Base', BALANCEBASE.toFixed(2));
        }
        this.setDataContainersValueWithChangedStore();
      })
    );
    super.notifyFromGridRefresh(gridView, dataStoreRequest);
  }

  notifyFromDataChange(item: any, vcrIndex?: number) {
    if (item.data['columnName'] === 'OnlyOpen' && this.dataStore) {
      if (!this.dataStore.data['OnlyOpen'] && this.dataStore.data['OnlyOpen'] === 'N') {
        this.setExcludedFiltersToGrid('BP accounting_table', ['OnlyOpen']);
      } else {
        this.removeExcludedFiltersToGrid('BP accounting_table');
      }
    }
    if (
      (item.data['columnName'] === 'C_BPartner_ID' && this.dataStore && this.dataStore.data['C_BPartner_ID']) ||
      (item.data['columnName'] === 'Type_piece' && this.dataStore && this.dataStore.data['Type_piece'])
    ) {
      this.setBPartnerInfos();
    }
    if (this.dataStore && this.dataStore.data['AD_Org_ID']) {
      const ad_orgIdValue = this.getFormStoreData('AD_Org_ID');
      if (ad_orgIdValue && ad_orgIdValue.id === 0) {
        this.setExcludedFiltersToGrid('BP accounting_table', ['AD_Org_ID']);
      } else {
        this.removeExcludedFiltersToGrid('BP accounting_table');
      }
    }
    super.notifyFromDataChange(item);
  }

  private setBPartnerInfos(): void {
    if (this.getFormStoreData('C_BPartner_ID')) {
      const vPartnerId = this.getFormStoreData('C_BPartner_ID')['id'];
      let vPieceType = undefined;
      if (this.getFormStoreData('Type_piece')) {
        vPieceType = this.getFormStoreData('Type_piece')['id'];
      }
      //? Response doesn't contain BASE_CR
      // const request: DataStoreRequest = {
      //   windowId: this.dataStore.key.windowId,
      //   record_id: this.dataStore.key.recordId.toString(),
      //   parent_constraint: undefined,
      //   compiereRequest: {
      //     tableName: 'C_BPartner',
      //     startRow: 0,
      //     endRow: 1,
      //     filterModel: {
      //       C_BPartner_ID: {
      //         filterType: CompiereDataGridFilterType.SET,
      //         values: [v_C_BPartner_ID],
      //         operators: [OperatorFilterType.EQUALS],
      //       },
      //     },
      //   },
      // };
      // this.store
      //   .getDataGrid(request)
      //   .subscribe((response) => this.setFormStoreData('BASE_CR', response.data[0]['BASE_CR']));

      //account
      this.specificDataService.getBPartnerBalanceData(vPartnerId, vPieceType).subscribe((response) => {
        if (!response) {
          this.dataStore.data['Account_ID'] = null;
          return;
        }

        if (response.isVendor === 'Y' && response.isCustomer === 'Y' && (vPieceType === 'Al' || !vPieceType)) {
          this.dataStore.data['Account_ID'] = null;
        } else {
          this.dataStore.data['Account_ID'] = response.accountId;
        }
      });
    }
  }
}
